import _ from 'lodash'

import { evaluateFormula } from './evaluation'
import { Query } from '../models/query'

/**
 * Resolves a select/tags field's dropdown options from its `optionSource`, in
 * precedence order: (1) `referenceList` — a firm-owned vocabulary
 * (`core_config_referenceList`); (2) `distinctValues` — values already stored on
 * the field (grouped query); (3) the field's static `enum`/`suggestions`
 * (handled by the caller). Platform-agnostic; the React hook lives in the
 * webapp, mobile calls these directly. See schemas/entities/config/referenceList.json.
 */

export const REFERENCE_LIST_ENTITY_TYPE = '/1.0/entities/metadata/core_config_referenceList.json'
const KEY_PATH = 'core_config_referenceList.key'
const ITEMS_PATH = 'core_config_referenceList.items'
const OWNER_FIRM_ID_PATH = 'owner.firm.entityId'
const DEFAULT_DISTINCT_VALUES_SIZE = 100

export interface IReferenceListOption {
  label: string
  value: string
}

export interface IOptionSource {
  /** Key of a firm-owned reference list to resolve options from. */
  referenceList?: string
  /** When true, suggest the distinct values already stored on the bound field. */
  distinctValues?: boolean
  /**
   * Edge path whose target's OWNER firm is the vocabulary authority — set this
   * for counterparty-owned entities where the record owner isn't the firm whose
   * list you want (e.g. a carrier-owned check-in execution whose list belongs to
   * the facility). Falls back to the entity's owner firm, then the session firm,
   * when unset or unresolvable. See resolveReferenceListFirmId.
   */
  referenceListFirmEdge?: string
}

/**
 * Reads a field's `optionSource`, from an explicit prop or (the common case) the
 * bound schema property via the `dataSchema` frames context.
 */
export const getOptionSource = (props: { optionSource?: IOptionSource; frames?: any }): IOptionSource | undefined => {
  if (props?.optionSource) {
    return props.optionSource
  }
  const dataSchema = props?.frames?.getContext?.('dataSchema')
  return dataSchema?.optionSource
}

/** Convenience: the reference-list key of a field's optionSource, if any. */
export const getReferenceListKey = (props: { optionSource?: IOptionSource; frames?: any }): string | undefined =>
  getOptionSource(props)?.referenceList

/** Resolves the entity-type id for the entity a field is bound to (for value queries). */
export const resolveEntityTypeId = (entity: any, entityType: string | undefined, valuePath: string[]): string => {
  if (entityType) {
    return entityType
  }
  const namespace = _.first(valuePath)
  const schema = _.find(entity?.schemas, (s) => _.get(s, 'metadata.namespace') === namespace)
  return schema?.id ?? `/1.0/entities/metadata/${namespace}.json`
}

/**
 * The firm that owns the entity being edited — the list resolves against this
 * firm, not the session firm, so a cross-firm switcher sees the owner firm's
 * vocabulary. Undefined for a brand-new entity (query falls back to the session firm).
 */
export const getEntityOwnerFirmId = (entity: any): string | undefined =>
  entity?.get?.(OWNER_FIRM_ID_PATH) ?? _.get(entity, OWNER_FIRM_ID_PATH)

/**
 * Resolves the firm whose reference list governs a field on the fallback ladder:
 * owner firm of the entity at `referenceListFirmEdge` (resolved store-first,
 * cache-first) → this entity's owner firm → session firm. Any miss degrades to
 * the next rung. See {@link IOptionSource.referenceListFirmEdge}.
 */
export const resolveReferenceListFirmId = (
  apis: any,
  entity: any,
  optionSource?: IOptionSource,
): Promise<string | undefined> => {
  const ownerFirmId = getEntityOwnerFirmId(entity)
  const edgePath = optionSource?.referenceListFirmEdge
  if (!edgePath) {
    return Promise.resolve(ownerFirmId)
  }
  const edge = entity?.get?.(edgePath) ?? _.get(entity, edgePath)
  const targetId = _.get(edge, 'entityId')
  const store = apis?.getStore?.()
  if (!targetId || !store?.getOrFetchRecord) {
    return Promise.resolve(ownerFirmId)
  }
  return Promise.resolve(store.getOrFetchRecord(targetId))
    .then((target: any) => getEntityOwnerFirmId(target) ?? ownerFirmId)
    .catch(() => ownerFirmId)
}

/** Maps a reference list's raw items to sorted, active {value,label} options. */
export const mapReferenceListItemsToOptions = (items: any[]): IReferenceListOption[] =>
  _.chain(items)
    .filter((item) => item?.isActive !== false && _.isString(item?.value) && item.value !== '')
    .sortBy((item) => (_.isFinite(item?.sortOrder) ? item.sortOrder : Number.MAX_SAFE_INTEGER))
    .map((item) => ({
      value: item.value,
      label: _.isString(item?.label) && item.label !== '' ? item.label : item.value,
    }))
    .value()

/**
 * Loads a firm's reference list by key as active {value,label} options, scoped to
 * `firmId` when given (else the session firm). Resolves to [] when no list is
 * configured, so callers can fall back to static suggestions / free-text.
 */
export const fetchReferenceListOptions = (
  apis: any,
  referenceListKey?: string,
  firmId?: string,
): Promise<IReferenceListOption[]> => {
  if (!referenceListKey) {
    return Promise.resolve([])
  }
  const query = new Query(apis)
    .setEntityType(REFERENCE_LIST_ENTITY_TYPE)
    .setFilters([{ type: 'match', path: KEY_PATH, value: referenceListKey }])
    .setSize(1)
    .setDebug({ context: 'referenceListOptions' })
  if (firmId) {
    query.setFirm(firmId)
  }
  return query
    .getCollection()
    .find()
    .then((entities: any[]) => mapReferenceListItemsToOptions(_.get(_.first(entities), ITEMS_PATH) ?? []))
    .catch(() => [])
}

/** Maps grouped-query buckets to distinct {value,label} options. */
export const mapDistinctBuckets = (buckets: any[]): IReferenceListOption[] =>
  _.compact(
    _.map(buckets, (bucket) => {
      const value = bucket?.data
      return _.isString(value) && value ? { label: value, value } : null
    })
  )

/**
 * Loads the distinct values already stored on a field (grouped query on the bound
 * path) so it can self-suggest from prior usage, scoped to `firmId` when given.
 * Resolves to [] when there is no value path.
 *
 * Note: array-of-primitive fields key under the bare dotted path
 * (`core_yms_trailer.tags`) in the EQL registry — no `[*]` suffix (that belongs
 * to the separate search-schema path system).
 */
export const fetchDistinctFieldValues = (
  apis: any,
  entityType: string,
  valuePath: string[],
  size?: number,
  firmId?: string,
): Promise<IReferenceListOption[]> => {
  if (_.isEmpty(valuePath) || !entityType) {
    return Promise.resolve([])
  }
  const query = new Query(apis)
    .setEntityType(entityType)
    .setGroups([{ path: valuePath.join('.'), type: 'field' }])
    .setShouldIncludeLeafEntities(false)
    .setSize(size ?? DEFAULT_DISTINCT_VALUES_SIZE)
    .setDebug({ context: 'optionSource--distinctValues' })
  if (firmId) {
    query.setFirm(firmId)
  }
  return query
    .getCollection()
    .find()
    .then((buckets: any[]) => mapDistinctBuckets(buckets))
    .catch(() => [])
}

/**
 * Normalizes creatable multi-select values via a `transform` formula (e.g.
 * 'UPPER(TO_IDENTIFIER(arg0))') — deduped, empties dropped. Non-array values pass
 * through unchanged.
 */
export const applyTagTransform = (transform: string | undefined, values: any): any => {
  if (!transform || !_.isArray(values)) {
    return values
  }
  const next = _.uniq(_.map(values, (value) => (_.isString(value) ? evaluateFormula(transform, value) ?? value : value)))
  return _.reject(next, (value) => value === '')
}
